import {useCallback, useEffect, useRef} from 'react'

// A save fires this long after the last change (TK-3179 AC 1.1: "within a
// few seconds of them pausing") ...
export const AUTOSAVE_DEBOUNCE_MS = 2500
// ... and no later than this after the first unsaved change, so someone who
// never pauses still saves at least every 15 seconds.
export const AUTOSAVE_MAX_WAIT_MS = 15000

/**
 * Debounced autosave with a max wait, plus the lifecycle flushes that keep an
 * unsaved answer from dying with the tab: hidden tab / app switch
 * (visibilitychange), page unload (pagehide), and unmount.
 *
 * `save` is read through a ref at fire time, so callers may pass a fresh
 * closure every render; the value it saves should likewise come from refs
 * rather than the closure (a stale recording in the payload would have the
 * API delete the real one). `saveOnPageHide` is the unload variant — the
 * document may not live long enough for a normal request to leave.
 */
const useAutosave = ({save, saveOnPageHide}) => {
  const saveRef = useRef(save)
  const saveOnPageHideRef = useRef(saveOnPageHide)
  const dirtyRef = useRef(false)
  const debounceTimerRef = useRef(null)
  const maxWaitTimerRef = useRef(null)

  saveRef.current = save
  saveOnPageHideRef.current = saveOnPageHide

  const clearTimers = () => {
    clearTimeout(debounceTimerRef.current)
    clearTimeout(maxWaitTimerRef.current)
    debounceTimerRef.current = null
    maxWaitTimerRef.current = null
  }

  /** Drops a pending autosave without saving: the caller is about to save on its own. */
  const cancel = useCallback(() => {
    clearTimers()
    dirtyRef.current = false
  }, [])

  /** Saves now if anything is pending. Resolves once the save settles (or immediately when clean). */
  const flush = useCallback(() => {
    clearTimers()

    if (!dirtyRef.current) return Promise.resolve()

    dirtyRef.current = false

    return Promise.resolve(saveRef.current())
  }, [])

  /** Marks the answer changed and (re)starts the debounce; the max-wait clock only starts once. */
  const schedule = useCallback(() => {
    dirtyRef.current = true

    clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = setTimeout(flush, AUTOSAVE_DEBOUNCE_MS)

    if (!maxWaitTimerRef.current)
      maxWaitTimerRef.current = setTimeout(flush, AUTOSAVE_MAX_WAIT_MS)
  }, [flush])

  const isDirty = useCallback(() => dirtyRef.current, [])

  useEffect(() => {
    // Switching tabs or apps hides the document while it's still alive, so a
    // normal request has time to leave; this is the flush that covers most
    // "I put my phone down" exits.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush()
    }

    // Unload: the document is going away. An unscheduled change goes out via
    // the keepalive variant when the caller has one, otherwise a normal save
    // is still better than nothing. Saves already handed off are not this
    // hook's to protect — the draft owns what's on the wire and re-sends it
    // on pagehide itself (useLifeStoryDraft.flushForUnload).
    const handlePageHide = () => {
      clearTimers()

      if (!dirtyRef.current) return

      dirtyRef.current = false

      if (saveOnPageHideRef.current) saveOnPageHideRef.current()
      else saveRef.current()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', handlePageHide)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', handlePageHide)
      // Leaving the screen with an unsaved change (any exit the explicit
      // handlers didn't cover) still saves it.
      flush()
    }
  }, [flush])

  return {schedule, flush, cancel, isDirty}
}

export default useAutosave
